import { useEffect, useState } from "react";
import { GetServerSideProps } from "next";
import { getServerSession } from "next-auth/next";
import { signIn } from "next-auth/react";
import AppInput from "@/components/forms/Fields/inputs/AppInput/AppInput";
import AppButton from "@/components/buttons/AppButton/AppButton";
import EyeIcon from "@/components/icons/Eye";
import { useRouter } from "next/router";
import encrypt from "@/helpers/encrypt";
import * as Yup from "yup";
import { Form, Formik, useFormik } from "formik";
import Link from "next/link";
import { useDispatch } from "react-redux";
import { setAuthData } from "@/redux/reducer/resetPasswordSlice";
import AuthLayout from "@/layout/AuthLayout/AuthLayout";
import AuthMobileLayout from "@/layout/AuthMobileLayout/AuthMobileLayout";
import apiFetch from "@/utils/token-guard/apiFetch";
import SupportSection from "@/components/SupportSection/SupportSection";
import { useNotify } from "@/hooks/useNotify";
import { authOptions } from "@/pages/api/auth/[...nextauth]";

const Signin: React.FC = () => {
  const router = useRouter();
  const dispatch = useDispatch();
  const notify = useNotify();

  const [passwordVisible, setPasswordVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<null | string>(null);

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required("Email is required")
      .matches(
        /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
        "Invalid email address",
      ),
    password: Yup.string()
      .required("Password is required")
      .min(8, "Password must be at least 8 characters long")
      .max(32, "Password can't be more than 32 characters")
      
  });

  const handleSubmit = async (values: { email: string; password: string }) => {
    setLoading(true);
    setError(null);
    const public_key = process.env.NEXT_PUBLIC_AUTH_PUBLIC_KEY as string;
    try {
      const encryptedPassword = await encrypt(values.password, { public_key });

      const result = await apiFetch("/api/auth/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username: btoa(values.email.toLowerCase()),
          password: encryptedPassword,
        }),
      });
      if (result.success && result.token) {
        sessionStorage.setItem("email", values.email.toLowerCase());
        sessionStorage.setItem("session_token", result.token);
        const username = values.email.toLowerCase();
        dispatch(
          setAuthData({
            username,
          }),
        );
        router.push("/login-confirm");
      }
      if (result.success && result.access_token) {
        const signInResult = await signIn("credentials", {
          flow: "direct",
          accessToken: result.access_token,
          refreshToken: result.refresh_token,
          redirect: false,
        });

        if (!signInResult?.error) {
          router.replace("/home");
        } else {
          notify(
            "error",
            "Login Failed!",
            "Check your username and password and try again",
          );
          setLoading(false);
        }
      }

      if (!result.success) {
        if (result.code === 598) {
          notify("error", "Connection Error", result.error);
        } else {
          notify(
            "error",
            "Login Failed!",
            "Check your username and password and try again",
          );
        }

        setLoading(false);
      }
    } catch (err) {
      setError(`An error occured ${err}`);
      setLoading(false);
    }finally{
      setLoading(false);

    }
  };

  const formik = useFormik({
    initialValues: { email: "", password: "" },
    validationSchema,
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    if (formik.dirty) {
      setError(null);
    }
  }, [formik.values.email, formik.values.password, formik.dirty]);

  return (
    <Formik
      initialValues={{ email: "", password: "" }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      validateOnBlur
    >
      {({
        errors,
        values,
        isValid,
        touched,
        handleChange,
        handleBlur,
        dirty,
        validateForm,
      }) => (
        <div className="flex flex-row  h-screen">
          <AuthMobileLayout className="md:hidden block">
            <div
              className="flex flex-col self-stretch bg-[#FFFFFF] min-h-[360px] py-10 mt-[-134px] mx-4 rounded-[16px]"
              style={{
                boxShadow: "0px 2px 12px #00000012",
              }}
            >
              <span className="text-[#1F1F1F] text-[26px] text-center mb-4">
                Sign in
              </span>

              <Form className="flex flex-col gap-5 w-full  justify-center self-stretch px-7 mb-4 ">
                <AppInput
                  name="email"
                  label="Email"
                  value={values.email}
                  onChange={handleChange("email")}
                  onBlur={handleBlur("email")}
                  onFocus={() => setError(null)}
                  error={touched.email && errors.email}
                  labelClass="!text-[#767676] "
                  hasfullWidth
                />
                <AppInput
                  name="password"
                  IconAfter={
                    <EyeIcon
                      onClick={() => setPasswordVisible(!passwordVisible)}
                      visible={passwordVisible}
                    />
                  }
                  iconClick={() => {
                    setPasswordVisible(!passwordVisible);
                  }}
                  type={passwordVisible ? "text" : "password"}
                  label="Password"
                  value={values.password}
                  onChange={handleChange("password")}
                  onBlur={handleBlur("password")}
                  onFocus={() => {
                    setError(null);
                  }}
                  error={touched.password && errors.password}
                  labelClass="!text-[#767676]"
                  hasfullWidth
                />
                <div className="flex flex-col gap-3 justify-center !text-center items-center ">
                  <Link href="/reset-password">
                    <span className="text-[13px] text-[#767676]">
                      Unlock Account/Forgot Password?
                    </span>
                  </Link>

                  <AppButton
                    className="!w-[135px] !h-[48px] bg-[#0B559F] !rounded-3xl"
                    label="Sign in"
                    variant="primary"
                    type="submit"
                    loading={loading}
                    disabled={!isValid || !dirty || loading || !!error}
                  />
                </div>
              </Form>

              <span className="text-[#767676] text-[14px] leading-6 text-center">
                Don’t have an account with us?{" "}
                <Link href="/signup">
                  <span className="text-[13px] text-[#0B559F]">Register </span>
                </Link>
              </span>
            </div>
          </AuthMobileLayout>

          <div className="w-1/2  relative md:flex justify-center  mx-auto my-0 hidden">
            <AuthLayout />
          </div>
          <div className="backgroundSection w-1/2 hidden md:flex flex-col ps-[130px] justify-center  text-center">
            <div className=" flex flex-col gap-5 items-start min-h-[485px]  w-[475px] max-xl:w-[375px] p-8 bg-white rounded-3xl">
              <span className="text-[32px] font-bold leading-9 text-[#1f1f1f]">
                Sign in
              </span>
              <div className="w-full text-start ">
                <Form className="flex flex-col gap-5">
                  <AppInput
                    name="email"
                    label="Email"
                    value={values.email}
                    onChange={handleChange("email")}
                    onBlur={handleBlur("email")}
                    onFocus={() => setError(null)}
                    error={touched.email && errors.email}
                    labelClass="!text-[#767676]"
                  />
                  <AppInput
                    name="password"
                    IconAfter={
                      <EyeIcon
                        onClick={() => setPasswordVisible(!passwordVisible)}
                        visible={passwordVisible}
                      />
                    }
                    iconClick={() => {
                      setPasswordVisible(!passwordVisible);
                    }}
                    type={passwordVisible ? "text" : "password"}
                    label="Password"
                    value={values.password}
                    onChange={handleChange("password")}
                    onBlur={handleBlur("password")}
                    onFocus={() => {
                      setError(null);
                    }}
                    error={touched.password && errors.password}
                    labelClass="!text-[#767676]"
                  />
                  <div className="flex justify-between items-center ">
                    <Link href="/reset-password">
                      <span className="text-[13px] text-[#767676]">
                        Unlock Account/Forgot Password?
                      </span>
                    </Link>
                  </div>
                  <div>
                    <AppButton
                      className="!w-[135px] !h-[48px] bg-[#0B559F] !rounded-3xl"
                      label="Sign in"
                      variant="primary"
                      type="submit"
                      loading={loading}
                      disabled={!isValid || loading || !!error}
                    />
                  </div>
                </Form>
                <div className="text-[13px] leading-6 my-5 flex  items-center text-[#767676]">
                  Don't have an account?
                  <Link href="/signup" className="text-[#0B559F] ms-1">
                    Register
                  </Link>
                </div>
              </div>
            </div>
            <SupportSection />
          </div>
        </div>
      )}
    </Formik>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const session = await getServerSession(context.req, context.res, authOptions);

  if (session) {
    return {
      redirect: {
        destination: "/home",
        permanent: false,
      },
    };
  }

  return {
    props: {},
  };
};

export default Signin;
